"use client";
import Image from "next/image";
import Link from "next/link";
import { SectionHeading, Caption } from "@/components/ui/editorial";

export const AboutSection = (): React.ReactNode => {
	const intro =
		"La ASOCIACIÓN EQUILIBRIO ha estado en constante evolución desde su nacimiento, en enero del 2021. Si bien es cierto en un primer momento todos los profesionales vinculados a ella estábamos centrados en desarrollar proyectos destinados al ámbito de la infancia y la familia dada nuestra implicación con el colectivo, en la actualidad y tras el largo camino recorrido e intercambio de pareceres por parte de numerosas profesionales del tercer sector, hemos definido la Asociación Equilibrio como una entidad de oportunidades tanto para profesionales con vocación como para todos los colectivos a quienes podemos aportar con nuestras ideas y ganas de cambio.";

	return (
		<section
			id="quienes-somos"
			className="px-4 xl:px-0 py-16"
			aria-label="Quiénes Somos"
			data-testid="who-we-are-section"
		>
			<div className="max-w-7xl mx-auto space-y-12 py-8">
				{/* Header */}
				<header className="container mx-auto relative z-10">
					<SectionHeading
						index="01"
						title="Quiénes Somos"
						kicker="Nuestra Identidad"
						as="h2"
					/>
					<p className="font-body text-lg mt-6 max-w-3xl leading-relaxed text-muted-foreground">
						Impulsamos proyectos socioculturales con enfoque de derechos,
						participación y evaluación de impacto.
					</p>
				</header>

				{/* Intro */}
				<p className="font-body text-lg leading-relaxed text-ink/80">{intro}</p>

				{/* Main Content Grid */}
				<div className="lg:grid lg:grid-cols-2 lg:gap-16 lg:items-start">
					{/* Contenido Principal */}
					<article
						className="flex flex-col space-y-6 text-lg"
						aria-labelledby="historia-heading"
					>
						<h2 id="historia-heading" className="sr-only">
							Historia de la Asociación Equilibrio
						</h2>

						<p className="font-body leading-relaxed text-ink/80">
							Como trabajadora social y especialista en intervención y mediación
							familiar, he estado dedicándome única y exclusivamente a la
							infancia y la familia durante 8 años aproximadamente. A
							posteriori, destiné mi intervención en materia de dependencia,
							ámbito muy distinto al anterior aunque igual de fascinante. En la
							actualidad me encuentro sumergida en el Servicio de Atención
							Integral a Mujeres y Menores Víctimas de Violencia de Género en la
							administración pública.
						</p>

						{/* Lista de valores */}
						<div
							className="border border-border rounded-xl p-8"
							role="region"
							aria-labelledby="valores-heading"
						>
							<h3
								id="valores-heading"
								className="font-display text-xl font-semibold text-ink mb-6"
							>
								Nuestros Valores
							</h3>
							<ul className="space-y-4 font-body text-base text-ink/80" role="list">
								<li className="flex items-start gap-4">
									<span className="text-terracotta mt-2 shrink-0 font-display text-sm">—</span>
									<span>Enfoque comunitario y participación activa</span>
								</li>
								<li className="flex items-start gap-4">
									<span className="text-terracotta mt-2 shrink-0 font-display text-sm">—</span>
									<span>Equidad, diversidad e inclusión</span>
								</li>
								<li className="flex items-start gap-4">
									<span className="text-terracotta mt-2 shrink-0 font-display text-sm">—</span>
									<span>Evaluación de impacto y mejora continua</span>
								</li>
							</ul>
						</div>

						<div className="space-y-6">
							<div className="border border-border rounded-xl p-6">
								<p className="font-body leading-relaxed text-ink/80 mb-4">
									Si quieres saber un poco más, ve a{" "}
									<Link
										href="/asociacion-equilibrio/lidera"
										className="font-semibold text-terracotta hover:text-terracotta/80 underline underline-offset-2 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-terracotta focus:ring-offset-2 rounded"
										aria-label="Lidera tu proyecto - página de liderazgo"
									>
										LIDERA TU PROYECTO
									</Link>
									.
								</p>
								<p className="font-display text-xl font-semibold text-ink">
									¡TE ESPERAMOS!
								</p>
							</div>
						</div>
					</article>

					{/* Imagen para móviles */}
					<div className="lg:hidden mt-8">
						<figure>
							<Image
								src="/assets/carrousel-formacion.jpg"
								alt="Sesión de formación y capacitación de la Asociación Equilibrio. Participantes en un taller de desarrollo profesional."
								className="w-full h-auto object-cover rounded-xl border border-border"
								width={500}
								height={800}
								loading="lazy"
								sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 500px"
							/>
							<Caption>Sesión de formación y capacitación de la Asociación Equilibrio</Caption>
						</figure>
					</div>

					{/* Imagen cover para desktop */}
					<div className="hidden lg:block h-full">
						<figure className="h-full">
							<Image
								src="/assets/carrousel-formacion.jpg"
								alt="Sesión de formación y capacitación de la Asociación Equilibrio. Participantes en un taller de desarrollo profesional."
								className="w-full h-full object-cover rounded-xl border border-border"
								width={500}
								height={800}
								loading="lazy"
								sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 500px"
							/>
							<Caption>Sesión de formación y capacitación de la Asociación Equilibrio</Caption>
						</figure>
					</div>
				</div>
			</div>
		</section>
	);
};

export default AboutSection;
